/**
 * Main App Component
 * Routes between auth and app screens
 */

import React, { useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { AuthScreen } from "./components/AuthScreen";
import { DoctorApp } from "./components/DoctorApp";
import { PatientApp } from "./components/PatientApp";
import { auth, db, initializeFirebase } from "./services/firebase";
import { logout } from "./services/auth";

export default function App() {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (_error) {
      setUser(null);
    }
  };

  // Initialize Firebase on app load
  useEffect(() => {
    initializeFirebase();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setIsAuthReady(true);
        return;
      }

      try {
        const userSnap = await getDoc(doc(db, "users", firebaseUser.uid));
        if (userSnap.exists()) {
          setUser({ id: firebaseUser.uid, ...userSnap.data() });
        } else {
          setUser(null);
        }
      } catch (_error) {
        setUser(null);
      } finally {
        setIsAuthReady(true);
      }
    });

    return () => unsubscribe();
  }, []);

  if (!isAuthReady) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50 text-slate-900">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center text-sm text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-emerald-50 text-slate-900">
      {!user ? (
        <AuthScreen onLogin={setUser} />
      ) : user.role === "doctor" ? (
        <DoctorApp user={user} onLogout={handleLogout} />
      ) : (
        <PatientApp user={user} onLogout={handleLogout} />
      )}
    </div>
  );
}
